import { CursorPaginator, OffsetPaginator, type Paginator } from '@/factories/paginator';
import type { ApiV1, PublicGalleryPublicationStatus } from '@teamworksdev/influencer/core';
import type { ArgumentsType } from '@vueuse/core';
import type { AxiosStatic } from 'axios';
import MockAdapter from 'axios-mock-adapter';

export function useMock(axios: AxiosStatic) {
	const mock = new MockAdapter(axios, { delayResponse: 250 });

	registerPublicGalleryShareMock();

	mock.onAny().passThrough();
}

// GALLERY PUBLIC-SHARE MOCKS
//
// Dev-only scaffolding for the five `/galleries/:uuid/public-share` endpoints
// (INF-4050), removed by INF-4051 once the backend endpoints are live.
//
// The consuming tickets call these through the SDK (`inflcr.api.galleries.*`),
// whose client is `fetch`-based (`InfluencerWeb.createCustomFetch`), so this
// intercepts `globalThis.fetch` rather than axios. Every request that is not a
// public-share route is delegated to the original `fetch`.

const PUBLIC_SHARE_PATH = /\/galleries\/([^/]+)\/public-share$/;
const PUBLIC_SHARE_RETRY_PATH = /\/galleries\/([^/]+)\/public-share\/retry$/;
const PUBLICATION_SETTLE_DELAY_MS = 1500;
const MOCK_RESPONSE_DELAY_MS = 250;
const FAILING_SLUG_TOKEN = 'fail';
const PUBLIC_GALLERY_BASE = import.meta.env.VITE_INTEGRATION_CALLBACK_BASE ?? '';

interface MockPublication {
	uuid: string;
	status: PublicGalleryPublicationStatus;
	url: string;
	slug: string;
	password_protected: boolean;
	password: string | null;
	expires_at: string | null;
	published_at: string | null;
	manifest_revision: number;
}

interface MockResult {
	status: number;
	body?: unknown;
}

// Seed a published gallery so the published state (badge / Unpublish) is testable
// without publishing first — the publication map is in-memory and resets on reload.
// Dev-only scaffolding (removed by INF-4051).
const SEEDED_PUBLICATIONS: Array<[string, MockPublication]> = [
	['01a10c02-c0bc-70de-a180-6e1d8207c90d', {
		uuid: 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
		status: 'published',
		url: `${PUBLIC_GALLERY_BASE}/gallery/public/dean-dont-delete-public-status`,
		slug: 'dean-dont-delete-public-status',
		password_protected: false,
		password: null,
		expires_at: null,
		published_at: new Date().toISOString(),
		manifest_revision: 1,
	}],
	['01a11594-bb42-731c-93d7-a46ba13bd130', {
		uuid: '3f9c2a7e-1b4d-4c8a-9e2f-5a6b7c8d9e0f',
		status: 'published',
		url: `${PUBLIC_GALLERY_BASE}/gallery/public/dean-dont-delete-settings-update`,
		slug: 'dean-dont-delete-settings-update',
		password_protected: true,
		password: 'secret',
		expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
		published_at: new Date().toISOString(),
		manifest_revision: 1,
	}],
];

const publications = new Map<string, MockPublication>(SEEDED_PUBLICATIONS);

function getGalleryUuid(pathname: string, pattern: RegExp) {
	return pathname.match(pattern)?.[1];
}

function parsePayload<T>(body: unknown): T {
	if (typeof body === 'string' && body.length > 0) return JSON.parse(body) as T;
	return (body as T) ?? ({} as T);
}

function toPublicGalleryShare(publication: MockPublication): ApiV1.PublicGalleryShare {
	return {
		uuid: publication.uuid,
		status: publication.status,
		url: publication.url,
		slug: publication.slug,
		password_protected: publication.password_protected,
		expires_at: publication.expires_at,
		manifest_revision: publication.manifest_revision,
	};
}

function toPublicGalleryShareSettings(
	publication: MockPublication,
): ApiV1.PublicGalleryShareSettings {
	const share = toPublicGalleryShare(publication);
	return publication.password_protected
		? {
			...share,
			password_protected: true,
			password: publication.password ?? '',
			published_at: publication.published_at,
		}
		: {
			...share,
			password_protected: false,
			password: null,
			published_at: publication.published_at,
		};
}

function settlePublication(galleryUuid: string, willFail = false) {
	setTimeout(() => {
		const publication = publications.get(galleryUuid);
		if (!publication || publication.status !== 'publishing') return;
		publication.status = willFail ? 'failed' : 'published';
		publication.published_at = publication.status === 'published' ? new Date().toISOString() : null;
	}, PUBLICATION_SETTLE_DELAY_MS);
}

/**
 * Resolves a public-share request against the in-memory publication state.
 * Returns `null` for any request that is not a public-share route, so the
 * caller can fall through to the real API.
 */
function resolvePublicShare(method: string, pathname: string, body: unknown): MockResult | null {
	// Check retry first: its pathname also matches the base path's prefix.
	const retryUuid = getGalleryUuid(pathname, PUBLIC_SHARE_RETRY_PATH);
	if (retryUuid) {
		if (method !== 'POST') return { status: 405, body: { code: 'METHOD_NOT_ALLOWED' } };

		const publication = publications.get(retryUuid);
		if (!publication || publication.status !== 'failed') {
			return { status: 409, body: { code: 'PUBLICATION_NOT_FAILED' } };
		}

		publication.status = 'publishing';
		settlePublication(retryUuid);

		const response: ApiV1.RetryPublicGalleryShareResponse = {
			data: toPublicGalleryShare(publication),
		};
		return { status: 202, body: response };
	}

	const galleryUuid = getGalleryUuid(pathname, PUBLIC_SHARE_PATH);
	if (!galleryUuid) return null;

	const publication = publications.get(galleryUuid);

	if (method === 'GET') {
		if (!publication || publication.status === 'unpublished') {
			return { status: 404, body: { code: 'NOT_FOUND' } };
		}

		const response: ApiV1.GetPublicGalleryShareResponse = {
			data: toPublicGalleryShareSettings(publication),
		};
		return { status: 200, body: response };
	}

	if (method === 'POST') {
		if (publication && publication.status !== 'unpublished') {
			return { status: 409, body: { code: 'PUBLICATION_ALREADY_ACTIVE' } };
		}

		const payload = parsePayload<ApiV1.PublishPublicGallerySharePayload>(body);
		const slug = payload.slug ?? '';
		const created: MockPublication = {
			uuid: crypto.randomUUID(),
			status: 'publishing',
			url: `${PUBLIC_GALLERY_BASE}/gallery/public/${slug}`,
			slug,
			password_protected: payload.password_required ?? false,
			password: payload.password_required ? payload.password ?? null : null,
			expires_at: payload.expires_at ?? null,
			published_at: null,
			manifest_revision: 1,
		};

		publications.set(galleryUuid, created);
		settlePublication(galleryUuid, slug.includes(FAILING_SLUG_TOKEN));

		const response: ApiV1.PublishPublicGalleryShareResponse = {
			data: toPublicGalleryShare(created),
		};
		return { status: 202, body: response };
	}

	if (method === 'PATCH') {
		if (!publication || publication.status === 'unpublished') {
			return { status: 404, body: { code: 'NOT_FOUND' } };
		}

		const payload = parsePayload<ApiV1.UpdatePublicGallerySharePayload>(body);

		if (payload.password_required !== undefined) {
			publication.password_protected = payload.password_required;
			if (payload.password_required) {
				if (payload.password) publication.password = payload.password;
			} else {
				publication.password = null;
			}
		}
		if ('expires_at' in payload) publication.expires_at = payload.expires_at ?? null;

		publication.status = 'publishing';
		publication.manifest_revision += 1;
		settlePublication(galleryUuid);

		const response: ApiV1.UpdatePublicGalleryShareResponse = {
			data: toPublicGalleryShare(publication),
		};
		return { status: 202, body: response };
	}

	if (method === 'DELETE') {
		if (publication) {
			publication.status = 'unpublished';
			publication.password = null;
		}
		return { status: 204 };
	}

	return { status: 405, body: { code: 'METHOD_NOT_ALLOWED' } };
}

function buildMockResponse(result: MockResult) {
	if (result.body === undefined) return new Response(null, { status: result.status });
	return new Response(JSON.stringify(result.body), {
		status: result.status,
		headers: { 'Content-Type': 'application/json' },
	});
}

function getRequestUrl(input: RequestInfo | URL) {
	if (typeof input === 'string') return input;
	if (input instanceof URL) return input.href;
	return input.url;
}

function delay(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function registerPublicGalleryShareMock() {
	const originalFetch = globalThis.fetch.bind(globalThis);

	globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
		const pathname = new URL(getRequestUrl(input), globalThis.location.origin).pathname;
		const method = (
			init?.method
			?? (typeof input === 'object' && 'method' in input ? input.method : 'GET')
		).toUpperCase();

		const body = init?.body
			?? (input instanceof Request && method !== 'GET' && method !== 'HEAD'
				? await input.clone().text()
				: undefined);

		const result = resolvePublicShare(method, pathname, body);
		if (!result) return originalFetch(input, init);

		await delay(MOCK_RESPONSE_DELAY_MS);
		return buildMockResponse(result);
	};
}

// MOCK UTILITIES
type MockReplyFunc = ArgumentsType<MockAdapter.RequestHandler['reply']>['0'];

// eslint-disable-next-line
function paginate<T extends object>(paginator: Paginator<T>): MockReplyFunc {
	if (paginator instanceof CursorPaginator) {
		return cursorPaginate(paginator);
	} else if (paginator instanceof OffsetPaginator) {
		return offsetPaginate(paginator);
	}
	throw new Error('paginator not recognized');
}

function cursorPaginate<T extends object>(paginator: CursorPaginator<T>): MockReplyFunc {
	return (config) => {
		const { per_page, cursor } = config.params;
		return {
			status: 200,
			data: paginator.getItems(per_page, cursor),
		};
	};
}

function offsetPaginate<T extends object>(paginator: OffsetPaginator<T>): MockReplyFunc {
	return (config) => {
		const { per_page, page } = config.params;
		return {
			status: 200,
			data: paginator.getItems(page, per_page),
		};
	};
}
